/**
 * Fleet Management Authentication Store using Zustand
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { api } from '../services/api';

// Fleet-specific roles — must match backend UserRole enum values exactly
export type FleetRole =
  | 'SUPER_ADMIN'
  | 'ADMIN'
  | 'FLEET_MANAGER'
  | 'FLEET_LOCATION_ADMIN'
  | 'FLEET_COORDINATOR'
  | 'FLEET_DRIVER'
  | 'FLEET_WORKSHOP'
  | 'FLEET_REPORTING';

/** Roles that can create/modify fleet processes */
export const FLEET_OPS_ROLES: FleetRole[] = [
  'SUPER_ADMIN',
  'ADMIN',
  'FLEET_MANAGER',
  'FLEET_LOCATION_ADMIN',
  'FLEET_COORDINATOR',
];

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: FleetRole;
  roles: FleetRole[];
  companyId?: string;
  companyName?: string;
  isActive: boolean;
  avatarUrl?: string;
}

interface LoginCredentials {
  email: string;
  password: string;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  activeRole: FleetRole | null;

  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  clearError: () => void;
  getActiveRole: () => FleetRole | null;
  hasRole: (role: FleetRole) => boolean;
  hasAnyRole: (roles: FleetRole[]) => boolean;
  isFleetOps: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      activeRole: null,

      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post<{
            accessToken: string;
            refreshToken: string;
            user: AuthUser;
          }>('/auth/login', credentials);

          const responseData = response.data;

          if (!responseData?.accessToken || !responseData?.refreshToken || !responseData?.user) {
            throw new Error('Ungültige Serverantwort');
          }

          const { accessToken, refreshToken, user: userData } = responseData;

          const user: AuthUser = {
            ...userData,
            isActive: userData.isActive ?? true,
            roles: userData.roles || (userData.role ? [userData.role] : []),
          };

          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', refreshToken);

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            activeRole: user.role,
          });
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Anmeldung fehlgeschlagen';
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: message,
          });
          throw error;
        }
      },

      logout: () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        set({
          user: null,
          isAuthenticated: false,
          error: null,
          activeRole: null,
        });
      },

      refreshUser: async () => {
        const token = localStorage.getItem('accessToken');
        if (!token) {
          set({ user: null, isAuthenticated: false });
          return;
        }

        try {
          const response = await api.get<AuthUser>('/auth/me');
          const userData = response.data;

          const user: AuthUser = {
            ...userData,
            isActive: userData.isActive ?? true,
            roles: userData.roles || (userData.role ? [userData.role] : []),
          };

          set({
            user,
            isAuthenticated: true,
          });
        } catch {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          set({
            user: null,
            isAuthenticated: false,
          });
        }
      },

      clearError: () => set({ error: null }),

      getActiveRole: () => {
        const { user, activeRole } = get();
        if (!user) return null;
        if (activeRole && user.roles?.includes(activeRole)) {
          return activeRole;
        }
        return user.role || null;
      },

      hasRole: (role: FleetRole) => {
        const { user } = get();
        if (!user || !user.roles) return false;
        return user.roles.includes(role);
      },

      hasAnyRole: (roles: FleetRole[]) => {
        const { user } = get();
        if (!user || !user.roles) return false;
        return roles.some((role) => user.roles.includes(role));
      },

      isFleetOps: () => {
        const { user } = get();
        if (!user) return false;
        return FLEET_OPS_ROLES.some((r) => user.roles?.includes(r) || user.role === r);
      },
    }),
    {
      name: 'fleet-auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        activeRole: state.activeRole,
      }),
    },
  ),
);
